import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, ListChecks, Loader2, Trash2 } from 'lucide-react';
import { api, downloadCsv, errorMessage, fmtDate, fmtInt, useQuery } from './lib';
import {
  BarList, Btn, EmptyState, ErrorState, Page, Pagination, Panel, RefreshButton, RowsSkeleton,
  SearchInput, SortTH, sortRows, StatCard, StatGrid, Table, TD, TR, useConfirm, usePagination, useSort,
} from './ui';

/* The Unasked waiting list: signups from Unasked's landing page, stored in
   this Supabase project (table unasked_waitlist) and read through
   /api/admin/unasked-waitlist. The page promised one thing: an email when
   Unasked opens. */

const LANGS = { en: 'English', it: 'Italiano', es: 'Español' };
const GETTERS = {
  email: (w) => w.email || '',
  lang: (w) => LANGS[w.lang] || w.lang || '',
  source: (w) => w.source || '',
  created_at: (w) => w.created_at || '',
};

const tally = (rows, keyOf) => {
  const counts = {};
  rows.forEach((w) => {
    const key = keyOf(w);
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
};

export default function UnaskedWaitlistPanel() {
  const { data, error, loading, refetch, updatedAt, setData } = useQuery('unasked-waitlist', async () => {
    const rows = await api.get('/admin/unasked-waitlist');
    return Array.isArray(rows) ? rows : [];
  });
  const [query, setQuery] = useState('');
  const [sort, onSort] = useSort('created_at', 'desc');
  const [confirm, confirmDialog] = useConfirm();
  const [removing, setRemoving] = useState(null);
  const list = useMemo(() => data || [], [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = list.filter((w) => !q || [w.email, w.source, LANGS[w.lang]].some((v) => (v || '').toLowerCase().includes(q)));
    return sortRows(rows, sort, GETTERS);
  }, [list, query, sort]);
  const pager = usePagination(filtered, 50, `${query}|${sort.field}|${sort.dir}`);

  const weekAgo = Date.now() - 7 * 86400000;
  const lastWeek = list.filter((w) => new Date(w.created_at || 0).getTime() >= weekAgo).length;
  const languages = useMemo(() => tally(list, (w) => LANGS[w.lang] || w.lang || 'Unknown'), [list]);
  const sources = useMemo(() => tally(list, (w) => w.source || 'Direct or unknown').slice(0, 6), [list]);

  const exportCsv = () => downloadCsv(
    `unasked_waitlist_${new Date().toISOString().slice(0, 10)}.csv`,
    ['Email', 'Language', 'Source', 'Signed up'],
    filtered.map((w) => [w.email, LANGS[w.lang] || w.lang, w.source || '', w.created_at]),
  );

  const remove = async (w) => {
    const ok = await confirm({
      title: `Remove ${w.email}?`,
      description: 'This deletes the signup from the Unasked waiting list for good. Use it for removal requests sent to hello@unasked.app.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setRemoving(w.id);
    try {
      await api.del(`/admin/unasked-waitlist/${w.id}`);
      setData((rows) => (rows || []).filter((row) => row.id !== w.id));
      toast.success(`Removed ${w.email}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Page
      title="Unasked waitlist"
      description="People who joined the waiting list on Unasked's landing page. The page promised to use their email only to say when Unasked opens."
      actions={(
        <>
          <Btn variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}>
            <Download className="h-3.5 w-3.5" /><span className="hidden sm:inline">Export CSV</span>
          </Btn>
          <RefreshButton onClick={refetch} loading={loading} updatedAt={updatedAt} />
        </>
      )}
    >
      {error && <ErrorState error={error} onRetry={refetch} />}

      <div className="grid gap-4 lg:grid-cols-3">
        <StatGrid className="grid-cols-2 lg:col-span-1 lg:grid-cols-1">
          <StatCard label="Signups" value={fmtInt(list.length)} loading={!data && loading} />
          <StatCard label="Last 7 days" value={fmtInt(lastWeek)} loading={!data && loading} />
        </StatGrid>
        <Panel title="Language">
          <BarList items={languages} empty="No signups yet" />
        </Panel>
        <Panel title="Where they came from">
          <BarList items={sources} empty="No signups yet" />
        </Panel>
      </div>

      <Panel flush>
        <div className="border-b border-border p-3">
          <SearchInput value={query} onChange={setQuery} placeholder="Email, source, language" />
        </div>
        {!data && loading ? (
          <RowsSkeleton rows={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon={ListChecks} title={list.length ? 'No signups match' : 'No signups yet'} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <SortTH label="Email" field="email" sort={sort} onSort={onSort} />
                  <SortTH label="Language" field="lang" sort={sort} onSort={onSort} className="hidden sm:table-cell" />
                  <SortTH label="Source" field="source" sort={sort} onSort={onSort} className="hidden md:table-cell" />
                  <SortTH label="Signed up" field="created_at" sort={sort} onSort={onSort} align="right" />
                  <th className="w-10"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {pager.slice.map((w) => (
                  <TR key={w.id}>
                    <TD><p className="truncate text-sm text-foreground">{w.email}</p></TD>
                    <TD className="hidden text-xs sm:table-cell">{LANGS[w.lang] || w.lang || '—'}</TD>
                    <TD className="hidden text-xs text-muted-foreground md:table-cell">{w.source || '—'}</TD>
                    <TD align="right" className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(w.created_at)}</TD>
                    <TD align="right">
                      <Btn
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => remove(w)}
                        disabled={removing === w.id}
                        aria-label={`Remove ${w.email}`}
                      >
                        {removing === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Btn>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
            <Pagination {...pager} noun="signups" />
          </>
        )}
      </Panel>
      {confirmDialog}
    </Page>
  );
}
